import React from "react";
import { fmtMoney } from "../benefitDetailFormat";

// Shared presentational primitives for the per-type benefit blocks. Blocks
// assemble a declarative `sections` array and hand it to DetailTable; the table
// owns all layout so every type renders consistently and readably.

export function humanizeEnum(value: string | null | undefined): string {
  if (!value) return "—";
  return String(value)
    .replace(/[_-]/g, " ")
    .replace(/\b\w/g, (character) => character.toUpperCase());
}

// Parse a decimal column (often a string like "25000.00") and money-format it,
// preserving the "—" semantics for null/blank/non-numeric values.
export function formatCurrency(value: number | string | null | undefined): string {
  if (value == null || value === "") return "—";
  const numeric = Number(value);
  return Number.isNaN(numeric) ? "—" : fmtMoney(numeric);
}

export function yesNo(value: boolean | null | undefined): string {
  return value ? "Yes" : "No";
}

export interface DetailRow {
  label: string;
  value: React.ReactNode;
}

export interface DetailSection {
  heading?: string;
  rows: DetailRow[];
  note?: string;
}

// Treats em-dash / null / empty string as "no value".
function rowHasValue(value: React.ReactNode): boolean {
  return value != null && value !== "—" && value !== "";
}

// Build a section, dropping rows with no value. Returns null when nothing is
// left so callers can filter empty sections out.
export function buildSection(
  heading: string | undefined,
  rows: DetailRow[],
  note?: string,
): DetailSection | null {
  const kept = rows.filter((row) => rowHasValue(row.value));
  if (kept.length === 0 && !note) return null;
  return { heading, rows: kept, note };
}

// Clean grouped key/value table: small uppercase section heading, then rows of
// muted label (left) + emphasized value (right) separated by hairline dividers.
export function DetailTable({ sections }: { sections: DetailSection[] }) {
  const visible = sections.filter((section) => section.rows.length > 0 || section.note);
  if (visible.length === 0) return <EmptyDetail />;

  return (
    <div className="space-y-4">
      {visible.map((section, sectionIndex) => (
        <div key={section.heading ?? sectionIndex}>
          {section.heading ? (
            <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground mb-1">
              {section.heading}
            </div>
          ) : null}
          <dl className="divide-y divide-border/60">
            {section.rows.map((row) => (
              <div key={row.label} className="flex items-baseline justify-between gap-4 py-1.5">
                <dt className="text-sm text-muted-foreground">{row.label}</dt>
                <dd className="text-sm font-medium text-right tabular-nums">{row.value}</dd>
              </div>
            ))}
          </dl>
          {section.note ? (
            <p className="mt-1.5 text-xs text-muted-foreground leading-relaxed">{section.note}</p>
          ) : null}
        </div>
      ))}
    </div>
  );
}

export function EmptyDetail({
  message = "No benefit details available",
}: {
  message?: string;
}) {
  return <div className="text-xs text-muted-foreground italic">{message}</div>;
}

export function LoadingDetail() {
  return (
    <div className="space-y-2 animate-pulse">
      <div className="h-3 w-24 bg-muted rounded" />
      <div className="space-y-1.5">
        <div className="h-5 bg-muted/60 rounded" />
        <div className="h-5 bg-muted/60 rounded" />
        <div className="h-5 bg-muted/60 rounded" />
      </div>
    </div>
  );
}

// Baseline plan attributes that exist on every plan record. Shown when a plan
// has no stored benefit detail (e.g. dental plans with no plan_benefits rows)
// so the section still conveys something useful.
export function PlanAttributesFallback({ plan }: { plan: any }) {
  const waitingPeriod =
    plan?.waitingPeriod != null && Number(plan.waitingPeriod) > 0
      ? `${plan.waitingPeriod} ${plan.waitingPeriodType || "days"}`
      : null;

  const section = buildSection("Plan", [
    { label: "Network", value: plan?.networkType || "—" },
    { label: "Metal Tier", value: plan?.metalTier || "—" },
    { label: "Waiting Period", value: waitingPeriod || "—" },
  ]);

  return (
    <div className="space-y-2">
      {section ? <DetailTable sections={[section]} /> : null}
      <p className="text-xs text-muted-foreground italic">
        Detailed benefit info isn’t available for this plan.
      </p>
    </div>
  );
}
